/* brand-link-override.js
 * Purpose: Rewrite vendor links from /collections/vendors?q=Vendor
 *          to the brand's actual collection: /collections/<handle>
 * Theme: Ella (works generically too)
 *
 * How it works:
 *  - Finds links that look like vendor links.
 *  - Converts the vendor name to a collection handle (handleize).
 *  - (Optional) remaps special cases where handle != collection handle.
 *  - (Optional) verifies collection existence by fetching /collections/<handle>.json
 *  - Updates hrefs. Includes a MutationObserver for AJAX-loaded products.
 */

(function () {
  if (window.__brandLinkOverrideInitialized) return;
  window.__brandLinkOverrideInitialized = true;

  // -------- Configuration --------

  // If you know any vendor → collection handle mismatches, add them here.
  // Key = handleized vendor, Value = exact collection handle.
  // Example: 'mamas-&-papas' → 'mamas-and-papas' (if your collection uses a different slug)
  var SPECIAL_HANDLE_MAP = {
    // 'mamas-papas': 'mamas-and-papas',
    // 'joolz': 'joolz-usa',
    // Pre-populate a few common ANB Baby brands if you like:
    'bugaboo': 'bugaboo',
    'nuna': 'nuna',
    'uppababy': 'uppababy',
    'doona': 'doona',
    'cybex': 'cybex',
    'stokke': 'stokke'
  };

  // If true, we’ll verify collection existence via /collections/<handle>.json.
  // This avoids dead links but adds small network requests (deduped/cached).
  var VERIFY_COLLECTION_EXISTS = true;

  // CSS selectors that commonly hold vendor/brand links in Ella:
  // We also pattern-match hrefs (safer across theme updates).
  var LINK_SELECTORS = [
    'a.product-vendor',
    '.prd .prd__info a[href*="/collections/vendors?q="]',
    '.productView .product__info a[href*="/collections/vendors?q="]',
    'a[href*="/collections/vendors?q="]'
  ];

  // -------- Helpers --------

  function handleize(str) {
    if (!str) return '';
    // Shopify-like handleize:
    // 1) Lowercase
    // 2) Replace apostrophes with nothing; replace non-alphanumeric with hyphens
    // 3) Collapse multiple hyphens
    // 4) Trim leading/trailing hyphens
    var s = String(str)
      .toLowerCase()
      .normalize('NFKD') // remove accents
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return s;
  }

  function getVendorFromHref(href) {
    // Extract vendor from /collections/vendors?q=Vendor Name
    try {
      var url = new URL(href, window.location.origin);
      if (!/\/collections\/vendors/i.test(url.pathname)) return null;
      var v = url.searchParams.get('q');
      return v ? decodeURIComponent(v) : null;
    } catch (e) {
      return null;
    }
  }

  function resolveCollectionHandle(vendor) {
    var h = handleize(vendor);
    if (SPECIAL_HANDLE_MAP[h]) return SPECIAL_HANDLE_MAP[h];
    return h;
  }

  var existenceCache = Object.create(null);
  function checkCollectionExistsOnce(handle) {
    if (!VERIFY_COLLECTION_EXISTS) {
      return Promise.resolve(true); // assume exists
    }
    if (handle in existenceCache) {
      return existenceCache[handle];
    }
    var p = fetch('/collections/' + encodeURIComponent(handle) + '.json', {
      method: 'GET',
      credentials: 'same-origin'
    })
      .then(function (res) {
        if (!res.ok) return false;
        return res.json().then(function (data) {
          // Heuristic: Shopify returns {collection: {...}} if it exists.
          return !!(data && data.collection && data.collection.handle === handle);
        }).catch(function () {
          // Some themes block JSON; fallback to status check only:
          return true;
        });
      })
      .catch(function () {
        return false;
      });
    existenceCache[handle] = p;
    return p;
  }

  function rewriteLink(el) {
    if (!el || !el.href) return;
    if (el.dataset.brandLinkOverridden === '1') return; // idempotent

    var vendor = getVendorFromHref(el.href);
    if (!vendor) return;

    var handle = resolveCollectionHandle(vendor);
    var targetUrl = '/collections/' + handle;

    checkCollectionExistsOnce(handle).then(function (exists) {
      if (exists) {
        el.href = targetUrl;
        el.dataset.brandLinkOverridden = '1';
        el.setAttribute('rel', 'noopener'); // small hardening
      } else {
        // Fall back to original vendor page if collection missing
        el.dataset.brandLinkOverridden = '1';
      }
    });
  }

  function scan(scope) {
    scope = scope || document;
    var seen = new Set();
    LINK_SELECTORS.forEach(function (sel) {
      scope.querySelectorAll(sel).forEach(function (a) {
        if (seen.has(a)) return;
        seen.add(a);
        // Only touch links that actually point to vendors
        if (a.href && /\/collections\/vendors\?q=/.test(a.href)) {
          rewriteLink(a);
        }
      });
    });
  }

  // Initial pass
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }

  // Watch for dynamically inserted cards/sections (Ella uses AJAX in many spots)
  var mo = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      for (var j = 0; j < m.addedNodes.length; j++) {
        var node = m.addedNodes[j];
        if (node.nodeType === 1) {
          scan(node);
        }
      }
    }
  });
  mo.observe(document.documentElement || document.body, { childList: true, subtree: true });

  // Optional global for debugging in console:
  window.__brandLinkOverride = {
    rescan: function () { scan(document); },
    cache: existenceCache,
    map: SPECIAL_HANDLE_MAP,
    handleize: handleize
  };
})();
